import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/pages/home/components/VideoCard';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  all: {
    title: '全部影视资源',
    description: '番茄视频为您精选全部影视资源，涵盖电影、电视剧、动漫、综艺、纪录片等各类内容，免费在线观看高清视频。',
    keywords: '番茄视频,全部影视,免费在线观看,高清视频,西瓜视频免费,91在线视频,小蓝视频,久久视频',
  },
  dianying: {
    title: '电影免费在线观看',
    description: '番茄视频为您提供最新热门电影免费在线观看，汇聚西瓜视频免费、91在线视频、小蓝视频等平台高清电影资源，即点即播不缓冲。',
    keywords: '电影,免费在线观看,高清电影,番茄视频,西瓜视频免费,91在线视频,小蓝视频,最新电影',
  },
  dianshiju: {
    title: '电视剧全集免费在线观看',
    description: '番茄视频为您提供最新电视剧全集免费在线观看，热门国产剧、网络剧一网打尽，高清画质即点即播，追剧首选平台。',
    keywords: '电视剧,免费在线观看,电视剧全集,番茄视频,久久视频,91在线视频,国产剧,高清',
  },
  zongyi: {
    title: '综艺节目免费在线观看',
    description: '番茄视频汇集热门综艺节目免费在线观看，真人秀、音乐竞演、搞笑综艺应有尽有，高清流畅不缓冲。',
    keywords: '综艺,免费在线观看,综艺节目,番茄视频,真人秀,音乐竞演,西瓜视频免费',
  },
  dongman: {
    title: '动漫免费在线观看',
    description: '番茄视频提供最新国漫、日漫免费在线观看，斗罗大陆、完美世界等热门动漫持续更新，高清画质畅享二次元世界。',
    keywords: '动漫,免费在线观看,国漫,日漫,番茄视频,小蓝视频,高清动漫,斗罗大陆',
  },
  jilupian: {
    title: '纪录片免费在线观看',
    description: '番茄视频精选优质纪录片免费在线观看，自然探索、人文历史、科技前沿等优秀纪录片，高清画质拓眼界长知识。',
    keywords: '纪录片,免费在线观看,自然纪录片,人文纪录片,番茄视频,久久视频,高清',
  },
  duanju: {
    title: '短剧免费在线观看',
    description: '番茄视频为您提供最新热门短剧免费在线观看，甜宠、悬疑、喜剧等各类短剧精彩不断，轻松追剧不费时。',
    keywords: '短剧,免费在线观看,热门短剧,番茄视频,西瓜视频免费,高清短剧',
  },
  tiyu: {
    title: '体育赛事免费在线观看',
    description: '番茄视频为您提供NBA、世界杯、中超等热门体育赛事免费在线观看，高清直播精彩回放，体育迷的必备平台。',
    keywords: '体育,免费在线观看,NBA,世界杯,足球,篮球,番茄视频,小蓝视频,高清',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  const categoryData = categories.find((c) => c.slug === activeCategory);
  const meta = categoryMeta[activeCategory] || categoryMeta.all;

  const filteredVideos = useMemo(() => {
    let list = activeCategory === 'all'
      ? videos
      : videos.filter((v) => v.category === activeCategory);

    if (sortBy === 'hot') {
      list = [...list].sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
    } else if (sortBy === 'new') {
      list = [...list].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    } else {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }

    return list;
  }, [activeCategory, sortBy]);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${meta.title} | 番茄视频`,
    description: meta.description,
    url: `https://www.fanqieshipin.com/category/${activeCategory}`,
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: categoryData?.name || '全部' },
  ];

  return (
    <>
      <PageSEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`/category/${activeCategory}`}
        schemaData={[webPageSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <Breadcrumb items={breadcrumbItems} />

          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
            {categoryData?.name || '全部'} · 免费在线观看
          </h1>
          <p className="text-sm text-foreground-600 mb-6">{meta.description}</p>

          {/* Sub-category tabs */}
          <div className="flex items-center gap-3 overflow-x-auto pb-3 mb-6 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort options */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <p className="text-sm text-foreground-600">
              共 <strong className="text-foreground-900">{filteredVideos.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1">
              {[
                { key: 'hot', label: '最热' },
                { key: 'new', label: '最新' },
                { key: 'rating', label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-secondary-200 text-secondary-800'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Video Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 pb-12">
            {filteredVideos.map((video) => (
              <VideoCard key={video.id} {...video} />
            ))}
          </div>
          {filteredVideos.length === 0 && (
            <div className="text-center py-20 text-foreground-500">
              <i className="ri-inbox-line text-5xl mb-4 block"></i>
              <p className="text-lg">暂无相关内容</p>
              <p className="text-sm">请尝试其他分类</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}